/**
 * Configuração centralizada de mensagens de toast por entidade
 *
 * Este arquivo é a fonte única de verdade para todas as mensagens de toast
 * relacionadas a operações CRUD no sistema.
 *
 * Uso:
 *   import { getToastMessagesForTable } from '@/utils/toastMessagesConfig';
 *   const toastConfig = getToastMessagesForTable('obra_ambiente');
 *   showSuccess(toastConfig.createSuccess);
 */

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURAÇÕES POR ENTIDADE
// ═══════════════════════════════════════════════════════════════════════════

export const TOAST_MESSAGES_CONFIG = {
  // ─────────────────────────────────────────────────────────────────────────
  // OBRAS E SUB-ENTIDADES
  // ─────────────────────────────────────────────────────────────────────────
  obra: {
    entityName: 'Obra',
    entityNamePlural: 'Obras',
    entityGender: 'a',
    createSuccess: { title: 'Obra criada', description: 'A obra foi cadastrada com sucesso.' },
    updateSuccess: { title: 'Obra atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Obra excluída', description: 'A obra foi removida do sistema.' }
  },

  fornecedor: {
    entityName: 'Fornecedor',
    entityNamePlural: 'Fornecedores',
    entityGender: 'o',
    createSuccess: { title: 'Fornecedor criado', description: 'O fornecedor foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Fornecedor atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Fornecedor excluído', description: 'O fornecedor foi removido do sistema.' }
  },

  obra_area: {
    entityName: 'Área',
    entityNamePlural: 'Áreas',
    entityGender: 'a',
    createSuccess: { title: 'Área criada', description: 'A área foi cadastrada com sucesso.' },
    updateSuccess: { title: 'Área atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Área excluída', description: 'A área foi removida do sistema.' }
  },

  obra_ambiente: {
    entityName: 'Ambiente',
    entityNamePlural: 'Ambientes',
    entityGender: 'o',
    createSuccess: { title: 'Ambiente criado', description: 'O ambiente foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Ambiente atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Ambiente excluído', description: 'O ambiente foi removido do sistema.' }
  },

  obra_ponto: {
    entityName: 'Ponto',
    entityNamePlural: 'Pontos',
    entityGender: 'o',
    createSuccess: { title: 'Ponto criado', description: 'O ponto foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Ponto atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Ponto excluído', description: 'O ponto foi removido do sistema.' }
  },

  obra_produto: {
    entityName: 'Produto',
    entityNamePlural: 'Produtos',
    entityGender: 'o',
    createSuccess: { title: 'Produto adicionado', description: 'O produto foi vinculado ao ponto.' },
    updateSuccess: { title: 'Produto atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Produto removido', description: 'O produto foi desvinculado do ponto.' }
  },

  obra_servico: {
    entityName: 'Serviço',
    entityNamePlural: 'Serviços',
    entityGender: 'o',
    createSuccess: { title: 'Serviço adicionado', description: 'O serviço foi vinculado ao ponto.' },
    updateSuccess: { title: 'Serviço atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Serviço removido', description: 'O serviço foi desvinculado do ponto.' }
  },

  obra_cabo: {
    entityName: 'Cabo',
    entityNamePlural: 'Cabos',
    entityGender: 'o',
    createSuccess: { title: 'Cabo adicionado', description: 'O cabo foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Cabo atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Cabo removido', description: 'O cabo foi excluído do sistema.' }
  },

  obra_tubo: {
    entityName: 'Tubo',
    entityNamePlural: 'Tubos',
    entityGender: 'o',
    createSuccess: { title: 'Tubo adicionado', description: 'O tubo foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Tubo atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Tubo removido', description: 'O tubo foi excluído do sistema.' }
  },

  obra_projeto: {
    entityName: 'Projeto',
    entityNamePlural: 'Projetos',
    entityGender: 'o',
    createSuccess: { title: 'Projeto criado', description: 'O projeto foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Projeto atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Projeto excluído', description: 'O projeto foi removido do sistema.' }
  },

  // Alias para tabela "projeto" (usada por ObraProjetoDrawer)
  projeto: {
    entityName: 'Projeto',
    entityNamePlural: 'Projetos',
    entityGender: 'o',
    createSuccess: { title: 'Projeto criado', description: 'O projeto foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Projeto atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Projeto excluído', description: 'O projeto foi removido do sistema.' }
  },

  obra_envolvido: {
    entityName: 'Envolvido',
    entityNamePlural: 'Envolvidos',
    entityGender: 'o',
    createSuccess: { title: 'Envolvido vinculado', description: 'O envolvido foi vinculado à obra.' },
    updateSuccess: { title: 'Vínculo atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Envolvido desvinculado', description: 'O envolvido foi removido da obra.' }
  },

  obra_jornada: {
    entityName: 'Jornada',
    entityNamePlural: 'Jornadas',
    entityGender: 'a',
    createSuccess: { title: 'Jornada criada', description: 'O registro foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Jornada atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Jornada excluída', description: 'O registro foi removido do sistema.' }
  },

  // Sub-entidade de obra_jornada (usada por GenericInlineEntityManager)
  obra_jornada_ata: {
    entityName: 'Ata',
    entityNamePlural: 'Atas',
    entityGender: 'a',
    createSuccess: { title: 'Ata adicionada', description: 'A ata foi registrada com sucesso.' },
    updateSuccess: { title: 'Ata atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Ata excluída', description: 'A ata foi removida da jornada.' }
  },

  obra_andamento: {
    entityName: 'Andamento',
    entityNamePlural: 'Andamentos',
    entityGender: 'o',
    createSuccess: { title: 'Andamento criado', description: 'O andamento foi registrado com sucesso.' },
    updateSuccess: { title: 'Andamento atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Andamento excluído', description: 'O andamento foi removido do sistema.' }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // EMPREENDIMENTOS
  // ─────────────────────────────────────────────────────────────────────────
  empreendimento: {
    entityName: 'Empreendimento',
    entityNamePlural: 'Empreendimentos',
    entityGender: 'o',
    createSuccess: { title: 'Empreendimento criado', description: 'O empreendimento foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Empreendimento atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Empreendimento excluído', description: 'O empreendimento foi removido do sistema.' }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PESSOAS E USUÁRIOS
  // ─────────────────────────────────────────────────────────────────────────
  pessoa: {
    entityName: 'Pessoa',
    entityNamePlural: 'Pessoas',
    entityGender: 'a',
    createSuccess: { title: 'Pessoa criada', description: 'A pessoa foi cadastrada com sucesso.' },
    updateSuccess: { title: 'Pessoa atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Pessoa excluída', description: 'A pessoa foi removida do sistema.' }
  },

  perfil: {
    entityName: 'Perfil',
    entityNamePlural: 'Perfis',
    entityGender: 'o',
    createSuccess: { title: 'Perfil criado', description: 'O perfil foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Perfil atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Perfil excluído', description: 'O perfil foi removido do sistema.' }
  },

  departamento: {
    entityName: 'Departamento',
    entityNamePlural: 'Departamentos',
    entityGender: 'o',
    createSuccess: { title: 'Departamento criado', description: 'O departamento foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Departamento atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Departamento excluído', description: 'O departamento foi removido do sistema.' }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // COMERCIAL
  // ─────────────────────────────────────────────────────────────────────────
  oportunidade: {
    entityName: 'Oportunidade',
    entityNamePlural: 'Oportunidades',
    entityGender: 'a',
    createSuccess: { title: 'Oportunidade criada', description: 'A oportunidade foi cadastrada com sucesso.' },
    updateSuccess: { title: 'Oportunidade atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Oportunidade excluída', description: 'A oportunidade foi removida do sistema.' }
  },

  proposta: {
    entityName: 'Proposta',
    entityNamePlural: 'Propostas',
    entityGender: 'a',
    createSuccess: { title: 'Proposta criada', description: 'A proposta foi cadastrada com sucesso.' },
    updateSuccess: { title: 'Proposta atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Proposta excluída', description: 'A proposta foi removida do sistema.' }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // FINANCEIRO
  // ─────────────────────────────────────────────────────────────────────────
  lancamento: {
    entityName: 'Lançamento',
    entityNamePlural: 'Lançamentos',
    entityGender: 'o',
    createSuccess: { title: 'Lançamento criado', description: 'O lançamento foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Lançamento atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Lançamento excluído', description: 'O lançamento foi removido do sistema.' }
  },

  conta: {
    entityName: 'Conta',
    entityNamePlural: 'Contas',
    entityGender: 'a',
    createSuccess: { title: 'Conta criada', description: 'A conta foi cadastrada com sucesso.' },
    updateSuccess: { title: 'Conta atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Conta excluída', description: 'A conta foi removida do sistema.' }
  },

  categoria_contabil: {
    entityName: 'Categoria',
    entityNamePlural: 'Categorias',
    entityGender: 'a',
    createSuccess: { title: 'Categoria criada', description: 'A categoria foi cadastrada com sucesso.' },
    updateSuccess: { title: 'Categoria atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Categoria excluída', description: 'A categoria foi removida do sistema.' }
  },

  centro_custo: {
    entityName: 'Centro de Custo',
    entityNamePlural: 'Centros de Custo',
    entityGender: 'o',
    createSuccess: { title: 'Centro de custo criado', description: 'O centro de custo foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Centro de custo atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Centro de custo excluído', description: 'O centro de custo foi removido do sistema.' }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CATÁLOGO / PRODUTOS
  // ─────────────────────────────────────────────────────────────────────────
  produto: {
    entityName: 'Produto',
    entityNamePlural: 'Produtos',
    entityGender: 'o',
    createSuccess: { title: 'Produto criado', description: 'O produto foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Produto atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Produto excluído', description: 'O produto foi removido do sistema.' }
  },

  produto_classificacao: {
    entityName: 'Classificação',
    entityNamePlural: 'Classificações',
    entityGender: 'a',
    bulkUpdate: { title: 'Produtos classificados', description: 'Tipo atualizado com sucesso.' }
  },

  servico_tipo: {
    entityName: 'Tipo de Serviço',
    entityNamePlural: 'Tipos de Serviço',
    entityGender: 'o',
    createSuccess: { title: 'Tipo de serviço criado', description: 'O tipo foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Tipo de serviço atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Tipo de serviço excluído', description: 'O tipo foi removido do sistema.' }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // VISTORIAS
  // ─────────────────────────────────────────────────────────────────────────
  vistoria_tipo: {
    entityName: 'Tipo de Vistoria',
    entityNamePlural: 'Tipos de Vistoria',
    entityGender: 'o',
    createSuccess: { title: 'Tipo de vistoria criado', description: 'O tipo de vistoria foi cadastrado com sucesso.' },
    updateSuccess: { title: 'Tipo de vistoria atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Tipo de vistoria excluído', description: 'O tipo de vistoria foi removido do sistema.' }
  },

  obra_vistoria: {
    entityName: 'Vistoria',
    entityNamePlural: 'Vistorias',
    entityGender: 'a',
    createSuccess: { title: 'Vistoria adicionada', description: 'A vistoria foi adicionada à obra.' },
    updateSuccess: { title: 'Vistoria atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Vistoria removida', description: 'A vistoria foi removida da obra.' }
  },

  os_vistoria: {
    entityName: 'Vistoria da OS',
    entityNamePlural: 'Vistorias da OS',
    entityGender: 'a',
    createSuccess: { title: 'Vistoria adicionada à OS', description: 'A vistoria foi adicionada à ordem de serviço.' },
    updateSuccess: { title: 'Vistoria atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Vistoria removida da OS', description: 'A vistoria foi removida da ordem de serviço.' }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ATIVIDADES
  // ─────────────────────────────────────────────────────────────────────────
  atividade: {
    entityName: 'Atividade',
    entityNamePlural: 'Atividades',
    entityGender: 'a',
    createSuccess: { title: 'Atividade criada', description: 'A atividade foi adicionada.' },
    updateSuccess: { title: 'Atividade atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Atividade excluída', description: 'A atividade foi removida.' }
  },

  atividade_area: {
    entityName: 'Área',
    entityNamePlural: 'Áreas',
    entityGender: 'a',
    createSuccess: { title: 'Área criada', description: 'A área foi cadastrada com sucesso.' },
    updateSuccess: { title: 'Área atualizada', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Área excluída', description: 'A área foi removida do sistema.' }
  },

  atividade_tipo: {
    entityName: 'Tipo de Atividade',
    entityNamePlural: 'Tipos de Atividade',
    entityGender: 'o',
    createSuccess: { title: 'Tipo criado', description: 'O tipo de atividade foi cadastrado.' },
    updateSuccess: { title: 'Tipo atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Tipo excluído', description: 'O tipo de atividade foi removido.' }
  },

  atividade_participante: {
    entityName: 'Participante',
    entityNamePlural: 'Participantes',
    entityGender: 'o',
    createSuccess: { title: 'Participante adicionado', description: 'O participante foi adicionado à atividade.' },
    updateSuccess: { title: 'Participante atualizado', description: 'As alterações foram salvas.' },
    deleteSuccess: { title: 'Participante removido', description: 'O participante foi removido da atividade.' }
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// MENSAGENS PADRÃO (usadas como fallback e para erros)
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_TOAST_MESSAGES = {
  // Erros genéricos (description usa error.message se null)
  loadError: {
    title: 'Erro ao carregar',
    description: null
  },
  saveError: {
    title: 'Erro ao salvar',
    description: null
  },
  deleteError: {
    title: 'Erro ao excluir',
    description: null
  },
  deleteBlocked: {
    title: 'Exclusão bloqueada',
    description: null  // Usa result.message do checkChildren
  },

  // Validação
  validationRequired: {
    title: 'Campo obrigatório',
    descriptionTemplate: 'Informe {label} para continuar.'
  },

  // Ações em lote (GenericCRUD)
  batchDeleteSuccess: {
    title: 'Registros excluídos',
    descriptionTemplate: '{count} registro(s) foram removidos.'
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// FUNÇÃO PRINCIPAL - OBTENÇÃO COM FALLBACK INTELIGENTE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Obtém as mensagens de toast para uma tabela específica
 *
 * @param {string} tableName - Nome da tabela (ex: 'obra_ambiente')
 * @returns {Object} Configuração de toasts com mensagens de sucesso, erro e validação
 *
 * @example
 * const config = getToastMessagesForTable('obra_ambiente');
 * showSuccess(config.createSuccess);
 * // → Toast: "Ambiente criado" / "O ambiente foi cadastrado com sucesso."
 *
 * @example
 * // Tabela não configurada usa fallback
 * const config = getToastMessagesForTable('minha_nova_tabela');
 * // → Gera automaticamente: "Tabela criado" / "Registro cadastrado com sucesso."
 */
type ToastMessage = { title: string; description: string | null };
type ToastConfigEntry = Record<string, unknown>;

export const getToastMessagesForTable = (tableName: string | null | undefined): Record<string, unknown> => {
  if (!tableName) {
    return buildToastObject(DEFAULT_TOAST_MESSAGES, {});
  }

  // Se tem configuração específica, usa ela + defaults
  const configMap = TOAST_MESSAGES_CONFIG as Record<string, ToastConfigEntry>;
  if (configMap[tableName]) {
    const config = configMap[tableName];
    return buildToastObject(config, config);
  }

  // Fallback inteligente: gera mensagens baseadas no nome da tabela
  // Ex: "obra_ambiente" → "ambiente" → "Ambiente"
  const parts = tableName.split('_');
  const entityName = parts[parts.length - 1];
  const capitalizedName = entityName.charAt(0).toUpperCase() + entityName.slice(1);

  const fallbackConfig = {
    entityName: capitalizedName,
    entityNamePlural: `${capitalizedName}s`,
    entityGender: 'o',
    createSuccess: {
      title: `${capitalizedName} criado`,
      description: 'Registro cadastrado com sucesso.'
    },
    updateSuccess: {
      title: `${capitalizedName} atualizado`,
      description: 'Alterações salvas.'
    },
    deleteSuccess: {
      title: `${capitalizedName} excluído`,
      description: 'Registro removido.'
    }
  };

  return buildToastObject(fallbackConfig, fallbackConfig);
};

/**
 * Constrói objeto de toast com aliases para uso mais intuitivo
 * Aliases: create/update/delete → createSuccess/updateSuccess/deleteSuccess
 */
function buildToastObject(config: ToastConfigEntry, entityConfig: ToastConfigEntry): Record<string, unknown> {
  const base: Record<string, unknown> = {
    ...DEFAULT_TOAST_MESSAGES,
    ...config
  };

  // Adicionar aliases mais intuitivos para GenericDrawer/GenericCRUD
  return {
    ...base,
    // Aliases para sucesso (create → createSuccess)
    create: entityConfig.createSuccess || base.createSuccess || { title: 'Salvo', description: 'Registro criado com sucesso.' },
    update: entityConfig.updateSuccess || base.updateSuccess || { title: 'Salvo', description: 'Alterações salvas com sucesso.' },
    delete: entityConfig.deleteSuccess || base.deleteSuccess || { title: 'Excluído', description: 'Registro removido com sucesso.' },

    // Aliases para erros
    createError: base.saveError || { title: 'Erro ao criar', description: null },
    updateError: base.saveError || { title: 'Erro ao salvar', description: null },
    deleteError: base.deleteError || { title: 'Erro ao excluir', description: null },

    // Operações em lote (GenericCRUD)
    bulkUpdate: { title: 'Registros atualizados', description: 'Alterações aplicadas com sucesso.' },
    bulkDelete: { title: 'Registros excluídos', description: 'Os registros foram removidos.' },
    bulkUpdateError: { title: 'Erro ao atualizar', description: null },
    bulkDeleteError: { title: 'Erro ao excluir', description: null },

    // Inline entity (GenericInlineEntityManager)
    inlineAdd: { title: 'Item adicionado', description: '(Será salvo ao salvar o registro)' }
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS PARA TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Substitui placeholders em templates de descrição
 *
 * @param {string} template - Template com placeholders (ex: "{count} registro(s)")
 * @param {Object} values - Valores para substituir (ex: { count: 5 })
 * @returns {string} String com placeholders substituídos
 *
 * @example
 * replaceTemplate("{count} registro(s) de {entity}", { count: 3, entity: "obra" });
 * // → "3 registro(s) de obra"
 */
export const replaceTemplate = (
  template: string | null | undefined,
  values: Record<string, unknown>
): string => {
  if (!template) return '';
  return Object.entries(values).reduce<string>(
    (str, [key, value]) => str.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value)),
    template
  );
};

/**
 * Cria uma mensagem de toast a partir de um template
 *
 * @param {Object} toastConfig - Configuração com title e descriptionTemplate
 * @param {Object} values - Valores para substituir no template
 * @returns {Object} { title, description } pronto para showSuccess/showError
 */
export const createToastFromTemplate = (
  toastConfig: { title: string; descriptionTemplate?: string | null },
  values: Record<string, unknown>
): ToastMessage => {
  return {
    title: toastConfig.title,
    description: replaceTemplate(toastConfig.descriptionTemplate, values)
  };
};

const toastMessagesModule = {
  TOAST_MESSAGES_CONFIG,
  DEFAULT_TOAST_MESSAGES,
  getToastMessagesForTable,
  replaceTemplate,
  createToastFromTemplate
};

export default toastMessagesModule;
